import { useMemo } from 'react';
import type { HypothesisCreativeEntry, HypothesisGroup } from '../types/hypothesis';

interface UseHypothesisGroupingReturn {
  groups: HypothesisGroup[];
  noHypothesis: HypothesisCreativeEntry[];
  hypothesisCount: number;
}

const sortByLaunchStatus = (creatives: HypothesisCreativeEntry[]) =>
  [...creatives].sort((a, b) => {
    if (a.launch_status === 'launched' && b.launch_status !== 'launched') return -1;
    if (a.launch_status !== 'launched' && b.launch_status === 'launched') return 1;
    return 0;
  });

const useHypothesisGrouping = (
  creatives: HypothesisCreativeEntry[],
): UseHypothesisGroupingReturn => {
  return useMemo(() => {
    const groupMap = new Map<string, HypothesisCreativeEntry[]>();
    const noHypothesisList: HypothesisCreativeEntry[] = [];

    for (const creative of creatives) {
      if (!creative.hypothesis) {
        noHypothesisList.push(creative);
        continue;
      }

      const existing = groupMap.get(creative.hypothesis);
      if (existing) {
        existing.push(creative);
      } else {
        groupMap.set(creative.hypothesis, [creative]);
      }
    }

    const groups: HypothesisGroup[] = [];

    for (const [hypothesis, items] of groupMap) {
      const sectionForGroup = items.some((item) => item.section === 'this_week')
        ? 'this_week'
        : items.some((item) => item.section === 'upcoming')
          ? 'upcoming'
          : null;

      groups.push({
        hypothesis,
        section: sectionForGroup,
        creatives: sortByLaunchStatus(items),
      });
    }

    const sortByHypothesisName = (a: HypothesisGroup, b: HypothesisGroup) =>
      a.hypothesis.localeCompare(b.hypothesis);

    return {
      groups: groups.sort(sortByHypothesisName),
      noHypothesis: sortByLaunchStatus(noHypothesisList),
      hypothesisCount: groupMap.size,
    };
  }, [creatives]);
};

export default useHypothesisGrouping;
